import { IconTrend, IconTrendNegative, IconWarning, IconSpeed } from '@capitalise/ui-design-system';

export const InsightId = Object.freeze({
  debtorRisk: 'debtor-risk',
  largeDebtorBook: 'large-debtor-book',
  overdraftUtilised: 'overdraft-utilised',
  highFixedAssets: 'high-fixed-assets',
  insufficientCash: 'insufficient-cash',
  cashRich: 'cash-rich',
  poorLiquidity: 'poor-liquidity',
  highEquity: 'high-equity',
  recovery: 'qualified-bad-debt',
  refinanceProperty: 'refinance-property',
  financeProperty: 'finance-a-property',
  vatLiability: 'vat-liability',
  taxLiability: 'tax-liability',
  longDebtorDays: 'long-debtor-days',
  largeDebtorsToCash: 'large-debtors-to-cash',
  governmentSupport: 'government-support',
});

export const InsightQuestionnaireTerm = Object.freeze({
  [InsightId.debtorRisk]: null,
  [InsightId.largeDebtorBook]: 12,
  [InsightId.overdraftUtilised]: null,
  [InsightId.highFixedAssets]: 60,
  [InsightId.insufficientCash]: 60,
  [InsightId.cashRich]: null,
  [InsightId.poorLiquidity]: 60,
  [InsightId.highEquity]: 12,
  [InsightId.refinanceProperty]: 60,
  [InsightId.financeProperty]: 60,
  [InsightId.vatLiability]: 60,
  [InsightId.longDebtorDays]: 12,
  [InsightId.largeDebtorsToCash]: 12,
  [InsightId.governmentSupport]: 60,
  [InsightId.taxLiability]: 60,
});

export const InsightRank = Object.freeze({
  high: 'high',
  medium: 'medium',
  low: 'low',
  none: 'none',
});

export const InsightAction = Object.freeze({
  arrangeCall: 'arrange-call',
  recoveryService: 'recovery-service',
});

export const InsightActionLabel = Object.freeze({
  [InsightAction.arrangeCall]: 'Arrange a call',
  [InsightAction.recoveryService]: 'New Enquiry',
});

export const InsightCostTerm = Object.freeze({
  apr: 'apr',
  ofRecovery: 'of recovery',
  month: 'month',
  year: 'year',
});

export const InsightCostTermLabel = Object.freeze({
  [InsightCostTerm.apr]: 'APR',
  [InsightCostTerm.ofRecovery]: 'OF RECOVERY',
  [InsightCostTerm.month]: '/month',
  [InsightCostTerm.year]: '/year',
});

export const InsightSource = Object.freeze({
  balanceSheet: 'balance-sheet',
  profitLoss: 'profit-and-loss',
  bank: 'bank',
});

export const InsightTag = Object.freeze({
  cashflow: 'cashflow',
  fixedAssets: 'fixedAssets',
  largeDebtors: 'largeDebtors',
  affordability: 'affordability',
  cashRich: 'cashRich',
  taxesDue: 'taxesDue',
  longTermLiability: 'longTermLiability',
  creditLine: 'creditLine',
  insurance: 'insurance',
  recovery: 'recovery',
  debtRecovery: 'debtRecovery',
  debtorsRisk: 'debtorsRisk',
  property: 'property',
  capitalAllowances: 'capitalAllowances',
  researchAndDevelopment: 'researchAndDevelopment',
  creditScoreManagement: 'creditScoreManagement',
  newUserTag: 'newUserTag',
  innovation: 'innovation',
  recoveryLoanScheme: 'recoveryLoanScheme',
});

export const InsightTagLabel = Object.freeze({
  [InsightTag.cashflow]: 'Short term cashflow',
  [InsightTag.fixedAssets]: 'High fixed assets',
  [InsightTag.largeDebtors]: 'Large debtors',
  [InsightTag.affordability]: 'Affordability',
  [InsightTag.cashRich]: 'Cash Rich',
  [InsightTag.taxesDue]: 'Taxes due',
  [InsightTag.longTermLiability]: 'Long term liability',
  [InsightTag.creditLine]: 'Refinance credit line',
  [InsightTag.insurance]: 'Insurance',
  [InsightTag.recovery]: 'Recovery',
  [InsightTag.debtorsRisk]: 'Debtors Risk',
  [InsightTag.property]: 'Property',
  [InsightTag.debtRecovery]: 'Debt Recovery',
  [InsightTag.capitalAllowances]: 'Capital Allowances',
  [InsightTag.researchAndDevelopment]: 'R&D Tax',
  [InsightTag.creditScoreManagement]: 'Credit Management',
  [InsightTag.innovation]: 'Innovation',
  [InsightTag.recoveryLoanScheme]: 'Recovery Loan Scheme',
});

export const InsightNarrativeVarType = Object.freeze({
  currency: 'currency',
  string: 'string',
  number: 'number',
  grade: 'grade',
});

export const InsightSubtitleType = Object.freeze({
  risk: `What's the risk?`,
  opportunity: `What's the opportunity?`,
});

export const insightContainsHiddenTag = (tag) => {
  const insightTagsWithoutProductMatches = [InsightTag.recovery, InsightTag.creditScoreManagement];

  return insightTagsWithoutProductMatches.includes(tag);
};

export const isInsightActionValid = (type) =>
  Object.values(InsightAction).find((elem) => elem === type);
export const isInsightSourceValid = (type) =>
  Object.values(InsightSource).find((elem) => elem === type);

export const BusinessInsightTypes = Object.freeze({
  opportunity: 'opportunity',
  risk: 'risk',
  informational: 'informational',
});

export const BusinessInsightTypesLabel = Object.freeze({
  [BusinessInsightTypes.opportunity]: 'Opportunity',
  [BusinessInsightTypes.risk]: 'Risk',
  [BusinessInsightTypes.informational]: 'Insight',
});

export const RulesInsight = Object.freeze({
  ASSET_FINANCE_RULE: 'assetFinanceRule',
  BAD_DEBT_MULTI_DEBTORS_RULE: 'badDebtMultiDebtorsRule',
  BAD_DEBT_SINGLE_DEBTOR_RULE: 'badDebtSingleDebtorRule',
  CAPITAL_ALLOWANCES_RULE: 'capitalAllowancesRule',
  CASH_TREASURIES_RULE: 'cashTreasuriesRule',
  CASHFLOW_RISK_RULE: 'cashflowRiskRule',
  CT_FUNDING_RULE: 'ctFundingRule',
  DEBT_EQUITY_RULE: 'debtEquityRule',
  DEBT_WRITE_OFF_RULE: 'debtWriteOffRule',
  DEBTOR_DAYS_RULE: 'debtorDaysRule',
  DELAY_PAYMENTS_RULE: 'delayPaymentsRule',
  FINANCE_PROPERTY_RULE: 'financePropertyRule',
  HIGH_DEBTORS_RULE: 'highDebtorsRule',
  HIGH_GROWTH_RULE: 'highGrowthRule',
  INNOVATION_SECTORS_RULE: 'innovationSectorsRule',
  INSURE_RISKS_RULE: 'insureRisksRule',
  INTANGIBLE_ASSETS_RULE: 'intangibleAssetsRule',
  LATE_PAYMENT_MULTI_DEBTORS_RULE: 'latePaymentMultiDebtorsRule',
  LATE_PAYMENT_SINGLE_DEBTORS_RULE: 'latePaymentSingleDebtorsRule',
  RECOVERY_LOAN_RULE: 'recoveryLoanRule',
  REFINANCE_PROPERTY_RULE: 'refinancePropertyRule',
  RELEASE_CASH_RULE: 'releaseCashRule',
  SECTOR_RECOVERY_RULE: 'sectorRecoveryRule',
  TAX_CREDITS_RULE: 'taxCreditsRule',
  VAT_FUNDING_RULE: 'vatFundingRule',
  CASH_DECREASE_RULE: 'cashDecreaseRule',
  CASH_MANAGEMENT_RULE: 'cashManagementRule',
  CONSOLIDATE_DEBT_RULE: 'consolidateDebtRule',
  FACILITY_RENEWAL_RULE: 'facilityRenewalRule',
  RECOVERY_LOAN_SCHEME_RULE: 'recoveryLoanSchemeRule',
  REPAYMENT_HOLIDAY_RULE: 'repaymentHolidayRule',
  CREDIT_ENQUIRIES_RULE: 'creditEnquiriesRule',
  CREDIT_LIMIT_DECREASE_RULE: 'creditLimitDecreaseRule',
  CREDIT_LIMIT_INCREASE_RULE: 'creditLimitIncreaseRule',
  CREDIT_LIMIT_RULE: 'creditLimitRule',
  CREDIT_SCORE_BELOW_AVG_RULE: 'creditScoreBelowAvgRule',
  CREDIT_SCORE_DECREASE_RULE: 'creditScoreDecreaseRule',
  CREDIT_SCORE_INCREASE_RULE: 'creditScoreIncreaseRule',
  CREDIT_SCORE_RULE: 'creditScoreRule',
  INDUSTRY_CREDIT_SCORE_RULE: 'industryCreditScoreRule',
  INDUSTRY_PERFORMANCE_BETTER_RULE: 'industryPerformanceBetterRule',
  NEW_BUSINESS_RULE: 'newBusinessRule',
  NEW_CCJ_REGISTERED_RULE: 'newCcjRegisteredRule',
  PAYMENT_PERFORMANCE_DECREASING_RULE: 'paymentPerformanceDecreasingRule',
  PAYMENT_TREND_IMPROVED_RULE: 'paymentTrendImprovedRule',
  PAYMENT_TREND_INDUSTRY_RULE: 'paymentTrendIndustryRule',
  RECENT_CREDIT_SEARCHES_RULE: 'recentCreditSearchesRule',
  CUSTOMER_SCORE_DECREASE_RULE: 'customerScoreDecreaseRule',
  CUSTOMER_LIMIT_DECREASE_RULE: 'customerLimitDecreasedRule',
  CUSTOMER_LEGAL_NOTICE_RULE: 'customerLegalNoticeRule',
  CUSTOMER_PAYMENT_TREND_RULE: 'customerPaymentTrendRule',
});

export const RulesInsightColor = Object.freeze({
  [RulesInsight.PAYMENT_PERFORMANCE_DECREASING_RULE]: 'warning',
  [RulesInsight.NEW_CCJ_REGISTERED_RULE]: 'danger',
  [RulesInsight.CREDIT_LIMIT_DECREASE_RULE]: 'warning',
  [RulesInsight.CREDIT_LIMIT_INCREASE_RULE]: 'green',
  [RulesInsight.CREDIT_SCORE_DECREASE_RULE]: 'warning',
  [RulesInsight.CREDIT_SCORE_INCREASE_RULE]: 'green',
  [RulesInsight.PAYMENT_TREND_IMPROVED_RULE]: 'green',
  [RulesInsight.RECENT_CREDIT_SEARCHES_RULE]: 'green',
});

export const RulesInsightIcon = Object.freeze({
  [RulesInsight.PAYMENT_PERFORMANCE_DECREASING_RULE]: IconTrendNegative,
  [RulesInsight.NEW_CCJ_REGISTERED_RULE]: IconWarning,
  [RulesInsight.CREDIT_LIMIT_DECREASE_RULE]: IconSpeed,
  [RulesInsight.CREDIT_LIMIT_INCREASE_RULE]: IconSpeed,
  [RulesInsight.CREDIT_SCORE_DECREASE_RULE]: IconSpeed,
  [RulesInsight.CREDIT_SCORE_INCREASE_RULE]: IconSpeed,
  [RulesInsight.PAYMENT_TREND_IMPROVED_RULE]: IconTrend,
  [RulesInsight.RECENT_CREDIT_SEARCHES_RULE]: IconSpeed,
});

export const RulesInsightLabel = Object.freeze({
  [RulesInsight.PAYMENT_PERFORMANCE_DECREASING_RULE]: 'Payment pattern is worsening',
  [RulesInsight.NEW_CCJ_REGISTERED_RULE]: 'New negative event registered',
  [RulesInsight.CREDIT_LIMIT_DECREASE_RULE]: 'Credit limit has decreased',
  [RulesInsight.CREDIT_LIMIT_INCREASE_RULE]: 'Credit limit has increased',
  [RulesInsight.CREDIT_SCORE_DECREASE_RULE]: 'Credit score has decreased',
  [RulesInsight.CREDIT_SCORE_INCREASE_RULE]: 'Credit score has increased',
  [RulesInsight.PAYMENT_TREND_IMPROVED_RULE]: 'Payment pattern is improving',
  [RulesInsight.RECENT_CREDIT_SEARCHES_RULE]: 'Recent credit searches detected',
});
